import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { rolesApi } from '@/api/roles.api';
import { Select } from '@/components/ui/Select';
import { moduleAdminPath } from '@/config/moduleDefaults';
import { useActiveModule } from '@/hooks/useActiveModule';
import { useAppSelector } from '@/hooks/useAppSelector';
import { queryKeys, tierOptions } from '@/lib/query';
import { showApiError } from '@/utils/apiError';

export type AssignableRoleSelectProps = {
  value?: number;
  onChange: (roleId: number | undefined) => void;
  employeeId?: number;
  email?: string;
  enabled?: boolean;
  className?: string;
};

export function AssignableRoleSelect({
  value,
  onChange,
  employeeId,
  email,
  enabled = true,
  className,
}: AssignableRoleSelectProps) {
  const { t } = useTranslation(['org', 'common']);
  const hasEmail = Boolean(email?.trim());
  const activeModule = useActiveModule();
  const entryModule = useAppSelector((s) => s.auth.entryModule);
  const rolesAdminPath = moduleAdminPath(activeModule ?? entryModule ?? 'POS', 'roles');
  const { data: roles = [], isLoading, isError } = useQuery({
    queryKey: queryKeys.roles.assignable(employeeId),
    queryFn: async () => {
      try {
        return await rolesApi.listAssignable(
          employeeId != null ? { employeeId } : undefined,
        );
      } catch (err) {
        showApiError(err, t('errors.failedToLoadRoles', { ns: 'org' }));
        throw err;
      }
    },
    enabled,
    ...tierOptions('operational', { staleTime: 0 }),
  });

  const options = useMemo(
    () =>
      roles.map((role) => ({
        value: String(role.id),
        label: role.systemRole
          ? `${role.name} ${t('roles.assignable.builtInSuffix')}`
          : role.name,
      })),
    [roles, t],
  );

  const empty = !isLoading && !isError && options.length === 0;

  return (
    <div className={className}>
      <Select
        label={hasEmail ? t('roles.assignable.labelRequired') : t('roles.assignable.label')}
        placeholder={
          isLoading
            ? t('roles.assignable.loading')
            : empty
              ? t('roles.assignable.empty')
              : hasEmail
                ? t('roles.assignable.placeholderWithEmail')
                : t('roles.assignable.placeholderOptional')
        }
        options={options}
        disabled={isLoading || empty}
        value={value != null ? String(value) : ''}
        allowClear={!hasEmail}
        onChange={(v) => onChange(v ? Number(v) : undefined)}
      />
      {empty && (
        <p className="mt-1.5 text-xs text-slate-500">
          {t('roles.assignable.createRolesHint')}{' '}
          <Link to={rolesAdminPath} className="font-medium text-primary-600 hover:underline">
            {t('roles.assignable.adminRolesLink')}
          </Link>{' '}
          {t('roles.assignable.createRolesHintSuffix')}
        </p>
      )}
      {hasEmail && !empty && !value && (
        <p className="mt-1.5 text-xs text-slate-500">{t('roles.assignable.requiredWithEmail')}</p>
      )}
    </div>
  );
}
